import * as Sentry from "@sentry/react";
import { Location } from "history";
import { DateTime } from "luxon";

import Config from "@readai/common/config";
import { MOBILE_APP_PARAMS } from "@readai/common/constants/mobileApp";
import ROUTES from "@readai/common/routes";
import type {
  LanguageCode,
  LocationWithState,
  OAuthProviderId,
  UserSignupSource,
} from "@readai/common/types";
import {
  parseUrlWithParamsUrl,
  withQueryParamsUrl,
} from "@readai/common/utils";
import { parseReportInviteFromUrl } from "@readai/common/utils/parsingUtils";

const getCommonRedirectToPath =
  (type: "redirectTo" | "errorRedirectTo") =>
  (location?: Location, redirectToQueryParam?: string | null) => {
    const locationStateRedirectTo = (
      location?.state as {
        [key in "redirectTo" | "errorRedirectTo"]: {
          pathname: string;
          search: string;
        };
      }
    )?.[type];

    // in the off chance both a location state redirectTo and a redirectTo query param are available,
    // prioritize the location state
    if (locationStateRedirectTo?.pathname) {
      return encodeURIComponent(
        parseUrlWithParamsUrl(
          locationStateRedirectTo.pathname,
          locationStateRedirectTo.search,
        ),
      );
    }

    return encodeURIComponent(
      parseUrlWithParamsUrl(redirectToQueryParam ?? ""),
    );
  };

const getRedirectToPath = getCommonRedirectToPath("redirectTo");
const getErrorRedirectToPath = getCommonRedirectToPath("errorRedirectTo");

export const getRedirectUrls = (
  action: "signup" | "signin",
  method: OAuthProviderId | "email",
  queryParams: {
    [param: string]: string | null | undefined;
  },
  location?: Location,
  withHostname = false,
) => {
  const { redirectTo: queryParamsRedirectTo, ...otherQueryParams } =
    queryParams;

  const redirectTo = getRedirectToPath(location, queryParamsRedirectTo);
  const errorRedirectTo = getErrorRedirectToPath(
    location,
    queryParamsRedirectTo,
  );

  const returnUrlOverride = otherQueryParams?.[MOBILE_APP_PARAMS.RETURN_URL];
  // override return url to handle mobile app logins
  const returnUrl = returnUrlOverride
    ? returnUrlOverride
    : withQueryParamsUrl(
        `${withHostname ? window.location.origin : ""}${ROUTES.OAUTH_RETURN}`,
        {
          ...otherQueryParams,
          redirectTo: encodeURIComponent(redirectTo),
          action,
          method,
          cb: Math.round(DateTime.now().toSeconds()),
        },
      );

  const errorUrl = withQueryParamsUrl(
    `${withHostname ? window.location.origin : ""}${ROUTES.OAUTH_RETURN}`,
    {
      ...otherQueryParams,
      errorRedirectTo: encodeURIComponent(errorRedirectTo),
      error: true,
      method,
      cb: Math.round(DateTime.now().toSeconds()),
    },
  );

  return { returnUrl, errorUrl, hasMobileOverride: Boolean(returnUrlOverride) };
};

export const getOauthUrl = (
  provider: OAuthProviderId,
  action: "signup" | "signin",
  queryParams: {
    [param: string]: string | null | undefined;
  },
  location?: Location,
  signupSource?: UserSignupSource | null,
  preferredLanguage?: LanguageCode,
  statsigStableId?: string,
): string => {
  const { returnUrl, errorUrl } = getRedirectUrls(
    action,
    provider,
    queryParams,
    location,
    true,
  );

  Sentry.addBreadcrumb({
    message: `Getting arth url for provider`,
    data: {
      provider,
      authConfigExists: !!Config?.auth,
      authConfigForProviderExists: !!Config?.auth?.[provider],
    },
    level: "log",
  });

  return withQueryParamsUrl(Config.auth[provider].AUTH_URL, {
    return_url: returnUrl,
    error_url: errorUrl,
    // Google includes an unchecked checkbox, so we'll ask separately for calendar access
    connect_calendar: provider !== "google",
    ...(action === "signup"
      ? {
          force_consent: true,
          signup_source: signupSource,
          preferred_language: preferredLanguage,
          statsig_stable_id: statsigStableId,
        }
      : null),
  });
};

export const getSignupSourceFromLocation = (
  location: LocationWithState,
): UserSignupSource | null => {
  // Any user signing up directly from a report link should be marked as a "share" signup
  const searchParams = new URLSearchParams(location.search);
  if (searchParams.get("sessionId")) {
    return "share";
  }

  if (!location.state?.redirectTo?.pathname) return null;

  const redirectTo = withQueryParamsUrl(
    location.state.redirectTo.pathname,
    location.state.redirectTo.search ?? {},
  );
  const reportInviteId = parseReportInviteFromUrl(
    decodeURIComponent(redirectTo),
  );
  return reportInviteId ? "share" : null;
};
